import React, { useState, useEffect } from "react";
import { VerificationService } from "../services/verificationService";
import { Session } from "../auth/session";
import { RemoteLogger } from "../services/remoteLogger";
import { Header } from "./Header";
import { IoArrowBackOutline } from "react-icons/io5";

interface VerificationListViewProps {
  onBack: () => void;
  onLogout: () => void;
  onSelect: (id: number) => void;
}

interface Verification {
  id: number;
  title: string;
  description: string;
  status: string | number;
  createdAt: string;
  type?: "Outgoing" | "Incoming" | string;
  template?: {
    name: string;
    verificationBaseType?: {
      title: string;
    };
    fields?: { label: string; key: string; displayOrder: number }[];
  };
  data?: Record<string, string>;
  isSuspicious?: boolean;
  requesterUser?: { username?: string; contact?: Contact };
  assignments?: {
    user?: { username?: string; contact?: Contact };
  }[];
}

interface Contact {
  firstName?: string;
  lastName?: string;
  email?: string;
}

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; dot: string; bg: string; border: string }
> = {
  pending: {
    label: "Pending",
    color: "text-amber-700",
    dot: "bg-amber-400",
    bg: "bg-amber-50",
    border: "border-amber-200",
  },
  verified: {
    label: "Verified",
    color: "text-emerald-700",
    dot: "bg-emerald-400",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
  },
  rejected: {
    label: "Rejected",
    color: "text-red-700",
    dot: "bg-red-400",
    bg: "bg-red-50",
    border: "border-red-200",
  },
};

const getStatusKey = (status: string | number): string => {
  if (typeof status === "number") {
    const map: Record<number, string> = { 1: "pending", 2: "verified", 3: "rejected" };
    return map[status] ?? "pending";
  }
  return status?.toLowerCase() ?? "pending";
};

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
};

// Same relative format as the app's list ("Just now", "5m ago", "3h ago", "2d ago"), then a date
const timeAgo = (iso: string): string => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
};

const contactName = (c?: Contact, fallback?: string): string =>
  (c && ([c.firstName, c.lastName].filter(Boolean).join(" ") || c.email)) || fallback || "";

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const VerificationListView: React.FC<VerificationListViewProps> = ({
  onBack,
  onLogout,
  onSelect,
}) => {
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "pending" | "verified" | "rejected">("all");

  useEffect(() => {
    const fetchVerifications = async () => {
      setLoading(true);
      try {
        const token = await Session.getToken();
        if (!token) {
          RemoteLogger.warn("No token found in VerificationListView, logging out...");
          onLogout();
          return;
        }

        RemoteLogger.info("[Verification] 📋 Fetching verification history...");
        const response = await VerificationService.listAll(token);
        RemoteLogger.info("[Verification] 📥 History received:", response);

        if (response.data) {
          // API envelope: { success, data: [...], pagination }
          // response.data is the envelope object, actual array is at response.data.data
          const envelope = response.data as any;
          const list = Array.isArray(envelope) ? envelope : (envelope?.data ?? []);
          setVerifications(Array.isArray(list) ? list : []);
        } else {
          if (response.status === 401) {
            RemoteLogger.warn("Session expired in VerificationListView, logging out...");
            onLogout();
          } else {
            setError(response.error || "Failed to load verifications");
          }
        }
      } catch {
        setError("An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchVerifications();
  }, []);

  const filtered =
    filter === "all"
      ? verifications
      : verifications.filter((v) => getStatusKey(v.status) === filter);

  const counts = {
    all: verifications.length,
    pending: verifications.filter((v) => getStatusKey(v.status) === "pending").length,
    verified: verifications.filter((v) => getStatusKey(v.status) === "verified").length,
    rejected: verifications.filter((v) => getStatusKey(v.status) === "rejected").length,
  };

  return (
    <div className="flex flex-col h-full bg-[#FCFCFC]">
      <Header title="Verifications"  />

      <button
        aria-label="Back"
        className="text-gray-500 hover:text-gray-700 mt-5 px-4"
        onClick={() => onBack()}
      >
        <IoArrowBackOutline size={16} />
      </button>

      {/* Filter tabs: app-style text tabs with a teal underline */}
      {!loading && !error && verifications.length > 0 && (
        <div className="px-4 pt-3 flex border-b border-[#E5E7EB]">
          {(["all", "pending", "verified", "rejected"] as const).map((tab) => (
            <button key={tab} onClick={() => setFilter(tab)} className="flex-1 flex flex-col items-stretch">
              <span
                className={`pb-2 text-[13px] font-medium capitalize text-center ${
                  filter === tab ? "text-[#1F2937]" : "text-[#6B7280] hover:text-[#1F2937]"
                }`}
              >
                {tab}
                <span className="ml-1 text-[11px] text-[#9CA3AF]">{counts[tab]}</span>
              </span>
              <span
                className={`h-0.5 rounded-sm transition-colors duration-200 ${
                  filter === tab ? "bg-[#18B9B1]" : "bg-transparent"
                }`}
              />
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2.5">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="relative w-10 h-10">
              <div className="w-10 h-10 border-2 border-gray-100 rounded-full" />
              <div className="absolute inset-0 w-10 h-10 border-2 border-t-[#33ccbf] rounded-full animate-spin" />
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-gray-700">Loading verifications</p>
              <p className="text-[11px] text-gray-400 mt-0.5">Fetching your history...</p>
            </div>
          </div>
        ) : error ? (
          <div className="mx-auto mt-8 max-w-xs">
            <div className="bg-white border border-red-100 rounded-2xl p-6 text-center shadow-sm">
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg
                  className="w-5 h-5 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  />
                </svg>
              </div>
              <p className="text-sm font-semibold text-gray-800">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 text-xs font-bold text-[#33ccbf] hover:underline"
              >
                Try again
              </button>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 bg-white border border-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
              <svg
                className="w-6 h-6 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-700">
              {filter === "all" ? "No verifications yet" : `No ${filter} verifications`}
            </p>
            <p className="text-xs text-gray-400 mt-1">Your verification history will appear here</p>
          </div>
        ) : (
          filtered.map((v, i) => {
            const statusKey = getStatusKey(v.status);
            const statusConf = STATUS_CONFIG[statusKey] ?? STATUS_CONFIG.pending;
            const isIncoming = v.type?.toLowerCase() === "incoming";

            const assignee = v.assignments?.[0]?.user;
            const assigneeName = assignee ? contactName(assignee.contact, assignee.username) : "";
            const otherAssignees = Math.max((v.assignments?.length ?? 0) - 1, 0);
            const requesterName = contactName(v.requesterUser?.contact, v.requesterUser?.username);
            // Avatar shows the other person, like the app: sender for incoming, assignee for outgoing
            const avatarName = (isIncoming ? requesterName : assigneeName) || requesterName || assigneeName;

            // Primary identifier: use the most meaningful submitted value as the card title
            // sender_address or first non-empty data value — NOT template metadata
            const cardTitle =
              v.data?.sender_address ||
              v.data?.domain ||
              v.data?.subject_line ||
              (v.data ? Object.values(v.data).find(Boolean) : null) ||
              v.title;

            // Subtitle: subject line if it exists and differs from cardTitle
            const cardSubtitle =
              v.data?.subject_line && v.data.subject_line !== cardTitle
                ? v.data.subject_line
                : null;

            return (
              <button
                key={v.id}
                onClick={() => onSelect(v.id)}
                style={{ animationDelay: `${i * 50}ms` }}
                className={`w-full text-left rounded-lg border p-3 flex items-start gap-2.5 transition-colors duration-200 animate-fadeIn ${
                  v.isSuspicious ? "bg-[#FFF5F5] border-[#FECACA]" : "bg-white border-[#E2E8F0]/60 hover:bg-gray-50"
                }`}
              >
                {/* Avatar: initials in the app's teal */}
                <div className="w-10 h-10 rounded-full bg-[#EBF8F7] flex items-center justify-center shrink-0">
                  <span className="text-[14px] font-semibold text-[#19BAB0]">{initials(avatarName)}</span>
                </div>

                <div className="flex-1 min-w-0">
                  {/* Row 1: title + status */}
                  <div className="flex items-center gap-1.5">
                    <h3 className="flex-1 min-w-0 text-[13px] font-semibold leading-snug text-[#111827] truncate">
                      {cardTitle}
                    </h3>
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${statusConf.color} ${statusConf.bg}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${statusConf.dot}`} />
                      {statusConf.label}
                    </span>
                  </div>

                  {/* Row 2: subject line */}
                  {cardSubtitle && (
                    <p className="mt-1 text-[12px] leading-tight text-[#656E81] truncate">{cardSubtitle}</p>
                  )}

                  {/* Row 3: assignee · time ago · id */}
                  <div className="mt-1 flex items-center gap-1.5 text-[12px] text-[#656E81] min-w-0">
                    {assigneeName && (
                      <>
                        <span className="truncate">
                          Assigned to <span className="font-medium text-[#374151]">{assigneeName}</span>
                          {otherAssignees > 0 && <span className="text-[#6B7280]"> +{otherAssignees}</span>}
                        </span>
                        <span className="text-gray-400">·</span>
                      </>
                    )}
                    <span className="whitespace-nowrap text-[#656E81]/80" title={formatDate(v.createdAt)}>
                      {timeAgo(v.createdAt)}
                    </span>
                    <span className="text-gray-400">·</span>
                    <span className="whitespace-nowrap text-[11px] text-[#6B7280]">#{v.id}</span>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out both;
        }
      `}</style>
    </div>
  );
};

export default VerificationListView;
